import {
	ReactPlugin,
	withAITracking,
} from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserHistory } from 'history';
import { Suspense, useEffect } from 'react';
import { BrowserRouter, RouterProvider } from 'react-router-dom';
import { setupApiAxiosInterceptors } from '@gov-nx/api/axios';
import { setOnUnauthorizedHandler } from '@gov-nx/api/errors/onUnauthorizedRegistry';
import { setLoggerEnv } from '@gov-nx/api/logger';
import { ErrorBoundary } from '@gov-nx/component/web/State/ErrorBoundary';
import { ErrorPage } from '@gov-nx/component/web/State/ErrorPage';
import {
	AppContextProvider,
	useAppContext,
} from '@gov-nx/core/app/context/AppContext';
import { ExtraConfigProvider } from '@gov-nx/core/app/extra-config/ExtraConfigProvider';
import { InitProvider } from '@gov-nx/core/app/init/InitProvider';
import { getQueryClient } from '@gov-nx/core/app/init/queryClient';
import { preparePageStructure } from '@gov-nx/module/page/definitions/PageMap';
import { rememberCurrentLocation } from '@gov-nx/utils/web/utils/sessionStoredUrl';
import { PageLoader } from './components/PageLoader';
import { extraConfig } from './extra-config/extraConfig';
import { appLoaderControl } from './helpers/loader';
import { registerSessionContinuity } from './helpers/sessionContinuity';
import { createAppRouter } from './routes/AppRoutes';

preparePageStructure();

const isLocalhost = window.location.hostname.includes('localhost');
const environment = '{{NX_CMS_ENV}}';
const finalEnvironment = environment.includes('NX')
	? isLocalhost
		? 'lcl'
		: 'prod'
	: environment;
const queryClient = getQueryClient();
const appExtraConfig = extraConfig(finalEnvironment);
const router = createAppRouter(appExtraConfig);

const instrumentationKey = '{{NX_INSTRUMENTATION_KEY}}';
const finalInstrumentationKey = instrumentationKey.includes('NX');

const reactPlugin = new ReactPlugin();
if (!finalInstrumentationKey) {
	const browserHistory = createBrowserHistory();
	const appInsights = new ApplicationInsights({
		config: {
			instrumentationKey: instrumentationKey,
			extensions: [reactPlugin],
			extensionConfig: {
				[reactPlugin.identifier]: { history: browserHistory },
			},
		},
	});
	appInsights.loadAppInsights();
}

const App = () => {
	const { appError } = useAppContext();

	useEffect(() => {
		setLoggerEnv(process.env.ENV);
	}, []);

	if (appError && isLocalhost) {
		// eslint-disable-next-line no-console
		console.error(
			'APP BREAKING ERROR! \nThis would block the user from doing anything if we were not on localhost!\n',
			appError
		);
	} else if (appError) {
		appLoaderControl.hide();
		return (
			<Suspense>
				<BrowserRouter>
					<ExtraConfigProvider extraConfig={appExtraConfig}>
						<ErrorPage message={appError.message} />
					</ExtraConfigProvider>
				</BrowserRouter>
			</Suspense>
		);
	}

	return (
		<ExtraConfigProvider extraConfig={appExtraConfig}>
			<ErrorBoundary>
				<InitProvider>
					<Suspense fallback={<PageLoader />}>
						<RouterProvider router={router} />
					</Suspense>
				</InitProvider>
			</ErrorBoundary>
		</ExtraConfigProvider>
	);
};

const AppWrapper = () => (
	<AppContextProvider loaderControl={appLoaderControl}>
		<QueryClientProvider client={queryClient}>
			<App />
		</QueryClientProvider>
	</AppContextProvider>
);

setupApiAxiosInterceptors();
setOnUnauthorizedHandler(rememberCurrentLocation);
registerSessionContinuity();

export default withAITracking(reactPlugin, AppWrapper);
